import { z } from 'zod';

/* ─────────── Сметы проекта (раздел 6.5 ТЗ, поток 2) ─────────── */

const Money = z.number().finite().nullable();

export const ESTIMATE_KINDS = {
  local: 'Локальная смета',
  object: 'Объектная смета',
  summary: 'Сводный сметный расчёт',
} as const;
export type EstimateKind = keyof typeof ESTIMATE_KINDS;
export const EstimateKindSchema = z.enum(Object.keys(ESTIMATE_KINDS) as [EstimateKind, ...EstimateKind[]]);

/** Виды версий сметы (СМ-11) */
export const ESTIMATE_VERSION_KINDS = {
  design: 'Проектная',
  expertise: 'После экспертизы',
  contract: 'Договорная',
  working: 'Рабочая',
  extra: 'На дополнительные работы',
} as const;
export type EstimateVersionKind = keyof typeof ESTIMATE_VERSION_KINDS;
export const EstimateVersionKindSchema = z.enum(
  Object.keys(ESTIMATE_VERSION_KINDS) as [EstimateVersionKind, ...EstimateVersionKind[]],
);

export const ESTIMATE_STATUSES = {
  draft: 'Черновик',
  review: 'На проверке',
  approved: 'Утверждена',
  archived: 'В архиве',
} as const;
export type EstimateStatus = keyof typeof ESTIMATE_STATUSES;
export const EstimateStatusSchema = z.enum(
  Object.keys(ESTIMATE_STATUSES) as [EstimateStatus, ...EstimateStatus[]],
);

/** Виды позиций сметы */
export const ESTIMATE_ITEM_TYPES = {
  work: 'Работа по норме',
  material: 'Материал по сборнику',
  equipment: 'Оборудование',
  price: 'Прайсовая позиция по КП',
  other: 'Прочие затраты',
} as const;
export type EstimateItemType = keyof typeof ESTIMATE_ITEM_TYPES;

/** Вид затрат по форме сметы */
export const COST_KINDS = { С: 'Строительные', М: 'Монтажные', ПР: 'Прочие', О: 'Оборудование' } as const;

const MoneyBlock = z.object({
  direct: Money,
  materials: Money,
  wages: Money,
  machinistPay: Money,
  machines: Money,
  equipment: Money,
  amount: Money,
});
export type EstimateMoney = z.infer<typeof MoneyBlock>;

/** Уровень денег для смет и цен единицы: «Акты и счета» (суммы бюджета — уровень «все деньги») */
export const ESTIMATE_MONEY = 'acts_invoices' as const;

export const EstimateTotals = z.object({
  construction: Money,
  installation: Money,
  other: Money,
  equipment: Money,
  amount: Money,
  wages: Money,
  laborHours: z.number(),
});
export type EstimateTotals = z.infer<typeof EstimateTotals>;

export const EstimateSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  objectId: z.uuid().nullable(),
  objectName: z.string().nullable(),
  parentId: z.uuid().nullable(),
  rootId: z.uuid(),
  kind: EstimateKindSchema,
  code: z.string(),
  name: z.string(),
  drawings: z.string().nullable(),
  versionNo: z.number().int(),
  versionKind: EstimateVersionKindSchema,
  isCurrent: z.boolean(),
  status: EstimateStatusSchema,
  priceLevel: z.string().nullable(),
  normBase: z.string().nullable(),
  totals: EstimateTotals,
  itemsCount: z.number().int(),
  budgetLineId: z.uuid().nullable(),
  budgetLineName: z.string().nullable(),
  source: z.string(),
  hasIssues: z.boolean(),
  updatedAt: z.string(),
});
export type EstimateSummary = z.infer<typeof EstimateSummary>;

export const EstimateItemRow = z.object({
  id: z.uuid(),
  sectionId: z.uuid().nullable(),
  number: z.number().int(),
  type: z.enum(Object.keys(ESTIMATE_ITEM_TYPES) as [EstimateItemType, ...EstimateItemType[]]),
  code: z.string().nullable(),
  localCode: z.string().nullable(),
  name: z.string(),
  source: z.string().nullable(),
  sourceNote: z.string().nullable(),
  unit: z.string(),
  quantity: z.number(),
  quantityExpr: z.string().nullable(),
  costKind: z.string(),
  unit_: MoneyBlock,
  total: MoneyBlock,
  priceIndex: z.number().nullable(),
  normItemId: z.uuid().nullable(),
  budgetLineId: z.uuid().nullable(),
  noVolumeSource: z.boolean(),
  /** Строка ведомости объёмов проекта, из которой взят объём (СМ-07) */
  takeoffId: z.uuid().nullable(),
  /** Позиция-аналог: стоимость единицы задана, ресурсов для пересчёта нет */
  analog: z.boolean(),
  resourcesCount: z.number().int(),
  coefficients: z.array(
    z.object({
      code: z.string().nullable(),
      name: z.string().nullable(),
      basis: z.string().nullable(),
      kind: z.string(),
      kLabor: z.number().nullable(),
      kMachines: z.number().nullable(),
      kMaterials: z.number().nullable(),
    }),
  ),
  /** Расхождения пересчёта при импорте по этой позиции */
  issues: z.array(z.string()),
});
export type EstimateItemRow = z.infer<typeof EstimateItemRow>;

export const EstimateItemResourceRow = z.object({
  id: z.uuid(),
  kind: z.string(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  price: Money,
  machinistPay: Money,
  norm: z.number().nullable(),
  quantity: z.number(),
  amount: Money,
  replacedCode: z.string().nullable(),
  replaceReason: z.string().nullable(),
});
export type EstimateItemResourceRow = z.infer<typeof EstimateItemResourceRow>;

export const EstimateCard = EstimateSummary.extend({
  objectCode: z.string().nullable(),
  constructionCode: z.string().nullable(),
  constructionName: z.string().nullable(),
  chapter: z.string().nullable(),
  designStage: z.string().nullable(),
  methodology: z.string().nullable(),
  priceZone: z.string().nullable(),
  compiledBy: z.string().nullable(),
  note: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  sections: z.array(
    z.object({
      id: z.uuid(),
      number: z.string().nullable(),
      name: z.string(),
      lockedBy: z.uuid().nullable(),
      lockedByName: z.string().nullable(),
      /** Позиций в разделе (хранится, est-10): по нему экран показывает «ещё N» без загрузки всех */
      itemsCount: z.number().int(),
      totals: EstimateTotals,
    }),
  ),
  items: z.array(EstimateItemRow),
  /** Постраничная выдача позиций (est-10): есть только при запросе с `limit`; итоги разделов — хранимые, по всем позициям */
  itemsPage: z
    .object({
      nextCursor: z.string().nullable(),
      total: z.number().int(),
    })
    .nullable()
    .optional(),
  /** Отчёт импорта: расхождения пересчёта с файлом (СМ-13) */
  importReport: z.object({
    fileName: z.string().nullable(),
    generator: z.string().nullable(),
    fileTotals: z.record(z.string(), z.number()).nullable(),
    issues: z.array(z.string()),
    /** Ручные правки после импорта (объёмы): смета больше не равна файлу */
    manual: z.array(z.string()),
    importedAt: z.string().nullable(),
    /** Округление как в программе-источнике: АВС — половина к чётному */
    rounding: z.enum(['half-up', 'half-even']),
    /** Позиции-аналоги: стоимость единицы из файла, ресурсов для пересчёта нет */
    analogItems: z.number().int(),
  }),
  /** Версии этой сметы */
  versions: z.array(
    z.object({
      id: z.uuid(),
      versionNo: z.number().int(),
      versionKind: EstimateVersionKindSchema,
      status: EstimateStatusSchema,
      isCurrent: z.boolean(),
      amount: Money,
      createdAt: z.string(),
    }),
  ),
  /** Входящие локальные сметы (для объектной и сводного расчёта) */
  children: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string(), amount: Money })),
  canEdit: z.boolean(),
  canApprove: z.boolean(),
});
export type EstimateCard = z.infer<typeof EstimateCard>;

export const EstimateInput = z.object({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  parentId: z.uuid().nullable().optional(),
  kind: EstimateKindSchema.optional(),
  code: z.string().trim().min(1).max(60),
  name: z.string().trim().min(2).max(500),
  drawings: z.string().trim().max(500).nullable().optional(),
  versionKind: EstimateVersionKindSchema.optional(),
  priceLevel: z.string().trim().max(120).nullable().optional(),
  priceZone: z.string().trim().max(40).nullable().optional(),
  normBase: z.string().trim().max(120).nullable().optional(),
  methodology: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(4000).nullable().optional(),
  budgetLineId: z.uuid().nullable().optional(),
  /** Глава сводного сметного расчёта (1–7) для объектной сметы (п. 8.2.56 НДЦС РК 8.01-08) */
  chapter: z.string().trim().max(10).nullable().optional(),
});
export type EstimateInput = z.infer<typeof EstimateInput>;

export const EstimatePatch = EstimateInput.partial().omit({ projectId: true });
export type EstimatePatch = z.infer<typeof EstimatePatch>;

export const EstimateImportResult = z.object({
  estimateId: z.uuid(),
  code: z.string(),
  name: z.string(),
  items: z.number().int(),
  totals: EstimateTotals,
  issues: z.array(z.string()),
  /** Что добавилось в нормативную базу из файла */
  normsAdded: z.number().int(),
  resourcesAdded: z.number().int(),
});
export type EstimateImportResult = z.infer<typeof EstimateImportResult>;

/** Сверка файла KENML 2.01 со сметой портала (СМ-13, ПМ-02): расхождения до тенге */
export const Kenml2DiffRow = z.object({
  what: z.string(),
  file: z.union([z.number(), z.string()]).nullable(),
  portal: z.union([z.number(), z.string()]).nullable(),
  diff: z.number().nullable(),
});
export const Kenml2ReconcileResult = z.object({
  ok: z.boolean(),
  tolerance: z.number(),
  docNumber: z.string(),
  fileItems: z.number().int(),
  matched: z.number().int(),
  totals: z.array(Kenml2DiffRow),
  items: z.array(Kenml2DiffRow),
  onlyInFile: z.array(z.string()),
  onlyInPortal: z.array(z.string()),
  codeMismatch: z.array(z.string()),
  /** Формальные замечания к файлу по правилам робота экспертизы */
  warnings: z.array(z.string()),
});
export type Kenml2ReconcileResult = z.infer<typeof Kenml2ReconcileResult>;

/** Строка ресурсной ведомости (СМ-15): по смете, объекту или проекту */
export const ResourceStatementRow = z.object({
  kind: z.string(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  price: Money,
  amount: Money,
  /** В скольких позициях встречается */
  items: z.number().int(),
  /** Разряд для труда */
  grade: z.number().nullable(),
});
export const ResourceStatement = z.object({
  scope: z.enum(['estimate', 'object', 'project']),
  title: z.string(),
  rows: z.array(ResourceStatementRow),
  totals: z.object({
    materials: Money,
    equipment: Money,
    machines: Money,
    laborHours: z.number(),
    machineHours: z.number(),
    wages: Money,
  }),
});
export type ResourceStatement = z.infer<typeof ResourceStatement>;

/** Цена позиции сметы для закупок и лимитов (ЗК-22, ЗК-04, ДС-06): без НДС, по уровню цен сметы */
export const EstimatePriceRow = z.object({
  itemId: z.uuid(),
  estimateId: z.uuid(),
  estimateCode: z.string(),
  estimateName: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  unitPrice: Money,
  amount: Money,
  priceLevel: z.string().nullable(),
  type: z.string(),
});
export type EstimatePriceRow = z.infer<typeof EstimatePriceRow>;

/** Сравнение двух версий сметы построчно (СМ-11) */
export const EstimateDiffRow = z.object({
  key: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  /** added — только в новой, removed — только в старой, changed — изменена, same — без изменений */
  change: z.enum(['added', 'removed', 'changed', 'same']),
  quantityA: z.number().nullable(),
  quantityB: z.number().nullable(),
  amountA: Money,
  amountB: Money,
  delta: Money,
});
export const EstimateDiff = z.object({
  a: z.object({
    id: z.uuid(),
    code: z.string(),
    versionNo: z.number().int(),
    versionKind: z.string(),
    amount: Money,
  }),
  b: z.object({
    id: z.uuid(),
    code: z.string(),
    versionNo: z.number().int(),
    versionKind: z.string(),
    amount: Money,
  }),
  rows: z.array(EstimateDiffRow),
  bySection: z.array(z.object({ section: z.string(), amountA: Money, amountB: Money, delta: Money })),
  /** Итоги разделов сметы (Раздел 1, 2…) — сумма позиций раздела в каждой версии; null у суммы — нет уровня денег */
  byEstimateSection: z.array(
    z.object({
      number: z.string().nullable(),
      name: z.string(),
      amountA: Money,
      amountB: Money,
      delta: Money,
      /** Позиций раздела: в старой и в новой версии */
      itemsA: z.number().int(),
      itemsB: z.number().int(),
    }),
  ),
  totals: z.object({
    added: z.number().int(),
    removed: z.number().int(),
    changed: z.number().int(),
    delta: Money,
  }),
});
export type EstimateDiff = z.infer<typeof EstimateDiff>;

/** Сметы проекта: сводка для карточки проекта и бюджета (БМ-01) */
export const ProjectEstimatesSummary = z.object({
  count: z.number().int(),
  amount: Money,
  byObject: z.array(
    z.object({
      objectId: z.uuid().nullable(),
      objectName: z.string(),
      amount: Money,
      count: z.number().int(),
    }),
  ),
  /** Суммы смет по разделам бюджета, к которым они привязаны */
  byBudgetLine: z.array(z.object({ budgetLineId: z.uuid(), amount: Money, count: z.number().int() })),
  unlinkedAmount: Money,
});
export type ProjectEstimatesSummary = z.infer<typeof ProjectEstimatesSummary>;

/** Параметры прайсовой позиции по КП (СМ-06): берутся из выпуска индексов, по которому считается смета */
export const PRICE_POSITION_DEFAULTS = {
  /** НДС в цене КП */
  vat: 1.16,
  /** Заготовительно-складские расходы: материалы и оборудование */
  storageMaterials: 1.012,
  storageEquipment: 1.0072,
  /** Коэффициент приведения К173 по НДЦС РК 8.04-07 (III квартал 2026 года) */
  index: 0.979,
  indexCode: 'П-Т2026-3',
  indexBasis: 'НДЦС РК 8.04-07-2026, п. 3.7 а), Кмат=0,979',
} as const;

export const EstimateCoefficientInput = z.object({
  code: z.string().trim().max(60).nullable().optional(),
  name: z.string().trim().min(2).max(500),
  basis: z.string().trim().min(2).max(500),
  kLabor: z.number().positive().max(100).nullable().optional(),
  kMachines: z.number().positive().max(100).nullable().optional(),
});

/** Новая позиция сметы: из нормы базы, из каталога ресурсов, прайсовая по КП или прочая */
export const EstimateItemInput = z.object({
  sectionId: z.uuid().nullable().optional(),
  /** work — по норме normItemId; material/equipment — из каталога resourceId; price — по КП; other — прочие */
  type: z.enum(['work', 'material', 'equipment', 'price', 'other']),
  normItemId: z.uuid().optional(),
  resourceId: z.uuid().optional(),
  code: z.string().trim().max(80).nullable().optional(),
  name: z.string().trim().max(1000).optional(),
  unit: z.string().trim().max(40).optional(),
  quantity: z.number().finite().min(0),
  quantityExpr: z.string().trim().max(200).nullable().optional(),
  costKind: z.enum(['С', 'М', 'ПР', 'О']).optional(),
  /** Прайсовая позиция: цена с НДС за единицу и что это — материал, оборудование или прочие */
  priceGross: z.number().finite().min(0).optional(),
  priceKind: z.enum(['material', 'equipment', 'other']).optional(),
  /** Обоснование: номер КП и поставщик (печатается в обосновании позиции) */
  source: z.string().trim().max(300).nullable().optional(),
  coefficients: z.array(EstimateCoefficientInput).max(10).optional(),
  /** Вставить после позиции */
  afterItemId: z.uuid().nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type EstimateItemInput = z.infer<typeof EstimateItemInput>;

/** Замена ресурса в позиции с обоснованием (СМ-06) */
export const ResourceReplaceInput = z.object({
  resourceId: z.uuid().optional(),
  code: z.string().trim().max(80).optional(),
  name: z.string().trim().max(500).optional(),
  unit: z.string().trim().max(40).optional(),
  price: z.number().finite().min(0).optional(),
  norm: z.number().finite().min(0).optional(),
  reason: z.string().trim().min(3).max(1000),
});
export type ResourceReplaceInput = z.infer<typeof ResourceReplaceInput>;

export const EstimateSectionInput = z.object({
  number: z.string().trim().max(20).nullable().optional(),
  name: z.string().trim().min(1).max(300),
});

/** Проверка сметы (СМ-17) */
export const EstimateCheckFinding = z.object({
  severity: z.enum(['error', 'warning', 'info']),
  code: z.string(),
  itemId: z.uuid().nullable(),
  itemNumber: z.number().int().nullable(),
  text: z.string(),
});
export type EstimateCheckFinding = z.infer<typeof EstimateCheckFinding>;

/** Пересчёт в новый уровень цен (СМ-02): отчёт изменений */
export const RepriceInput = z.object({
  releaseIds: z.array(z.uuid()).min(1).max(20),
  versionKind: EstimateVersionKindSchema.optional(),
});
export const RepriceReport = z.object({
  estimateId: z.uuid(),
  changed: z.array(
    z.object({
      itemNumber: z.number().int(),
      name: z.string(),
      before: Money,
      after: Money,
      resources: z.number().int(),
    }),
  ),
  unchanged: z.number().int(),
  missing: z.array(z.object({ code: z.string(), name: z.string() })),
  totalBefore: Money,
  totalAfter: Money,
});
export type RepriceReport = z.infer<typeof RepriceReport>;

/* ─────────── Сравнение КП со сметой (СМ-18, БМ-05) ─────────── */

export const OfferCompare = z.object({
  request: z.object({ id: z.uuid(), number: z.string(), title: z.string() }),
  offer: z.object({
    id: z.uuid(),
    supplierName: z.string(),
    total: Money,
    deliveryIncluded: z.boolean(),
  }),
  lines: z.array(
    z.object({
      lineId: z.uuid(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number(),
      /** Цена КП за единицу с НДС и без (НДС 16 %) */
      offerPrice: Money,
      offerPriceNet: Money,
      /** Сопоставленная позиция сметы; confidence — уверенность сопоставления, % */
      estimateItem: z
        .object({
          estimateId: z.uuid(),
          estimateCode: z.string(),
          itemId: z.uuid(),
          number: z.number().int(),
          code: z.string().nullable(),
          name: z.string(),
          unit: z.string(),
          group: z.enum(['materials', 'works', 'equipment', 'other']),
          unitPrice: Money,
          confidence: z.number().int(),
        })
        .nullable(),
      /** Отклонение цены КП без НДС от сметной: + дороже, − дешевле */
      diffPct: z.number().nullable(),
      /** Цены прошлых закупок компании по похожим названиям, без НДС (БМ-11) */
      history: z
        .object({
          count: z.number().int(),
          min: Money,
          last: Money,
          lastSelected: Money,
          lastSupplier: z.string().nullable(),
        })
        .nullable(),
    }),
  ),
  totals: z.object({
    offerNet: z.object({ materials: Money, works: Money, equipment: Money, other: Money, total: Money }),
    estimate: z.object({ materials: Money, works: Money, equipment: Money, other: Money, total: Money }),
    unmatched: z.number().int(),
  }),
  estimateIds: z.array(z.uuid()),
});
export type OfferCompare = z.infer<typeof OfferCompare>;

export const ProjectOffers = z.array(
  z.object({
    requestId: z.uuid(),
    number: z.string(),
    title: z.string(),
    stage: z.string(),
    offers: z.array(
      z.object({
        id: z.uuid(),
        supplierName: z.string(),
        total: Money,
        isSelected: z.boolean(),
        missing: z.number().int(),
      }),
    ),
  }),
);
export type ProjectOffers = z.infer<typeof ProjectOffers>;

/* ---------- История цен по поставщикам и плановые цены из лучших КП (БМ-11) ---------- */

export const PriceHistoryRow = z.object({
  /** offer — КП по заявке, receipt — проведённое поступление на склад */
  source: z.enum(['offer', 'receipt']),
  date: z.string().nullable(),
  supplier: z.string().nullable(),
  projectCode: z.string().nullable(),
  requestNumber: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number().nullable(),
  /** Цена за единицу без НДС */
  priceNet: z.number(),
  /** КП выбрано по заявке или поступление проведено */
  selected: z.boolean(),
  similarity: z.number(),
});
export type PriceHistoryRow = z.infer<typeof PriceHistoryRow>;

export const PriceHistory = z.object({
  name: z.string(),
  unit: z.string().nullable(),
  rows: z.array(PriceHistoryRow),
  summary: z.object({
    count: z.number().int(),
    min: z.number().nullable(),
    median: z.number().nullable(),
    last: z.number().nullable(),
    lastSelected: z.number().nullable(),
  }),
});
export type PriceHistory = z.infer<typeof PriceHistory>;

/**
 * Изменения сметы по запросам на изменение (СМ-12): какие запросы затрагивают смету, что изменилось против прежней
 * версии и что это значит для бюджета и договора. Суммы — null без уровня денег.
 */
export const EstimateChangeImpact = z.object({
  /** Запросы на изменение, к которым привязана эта смета (любая её версия) */
  changes: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      title: z.string(),
      status: z.string(),
      reason: z.string().nullable(),
      /** Разница по смете из самого запроса */
      costDelta: z.number().nullable(),
      revenueDelta: z.number().nullable(),
    }),
  ),
  /** Почему сделана эта версия и по какому запросу (СМ-12) */
  version: z.object({
    reason: z.string().nullable(),
    changeId: z.uuid().nullable(),
    changeNumber: z.string().nullable(),
  }),
  /** Прежняя версия, с которой сравнивается эта; null у первой версии */
  previous: z.object({ id: z.uuid(), versionNo: z.number().int(), versionKind: z.string() }).nullable(),
  /** Разница по позициям и итогу против прежней версии */
  diff: z
    .object({
      added: z.number().int(),
      removed: z.number().int(),
      changed: z.number().int(),
      delta: z.number().nullable(),
      rows: z.array(
        z.object({
          status: z.string(),
          name: z.string(),
          unit: z.string(),
          delta: z.number().nullable(),
        }),
      ),
    })
    .nullable(),
  /** Бюджет: утверждённые и ожидающие решения изменения по этим запросам (БМ-10) */
  budget: z
    .object({
      approvedDelta: z.number().nullable(),
      approvedCount: z.number().int(),
      proposedCount: z.number().int(),
    })
    .nullable(),
  /** Договоры: допсоглашения, оформленные по этим запросам */
  amendments: z.array(
    z.object({
      id: z.uuid(),
      contractId: z.uuid(),
      contractNumber: z.string(),
      number: z.number().int(),
      status: z.string(),
    }),
  ),
});
export type EstimateChangeImpact = z.infer<typeof EstimateChangeImpact>;

/** Комментарий к позиции сметы (СМ-09) */
export const EstimateItemComment = z.object({
  id: z.uuid(),
  body: z.string(),
  author: z.string().nullable(),
  createdAt: z.string(),
  resolved: z.boolean(),
  resolvedBy: z.string().nullable(),
  /** Свой комментарий или право править сметы — можно решить и убрать */
  canManage: z.boolean(),
});
export type EstimateItemComment = z.infer<typeof EstimateItemComment>;

/* ─────────── Ведомость объёмов проекта (СМ-07): строки ведомости ПСД и элементы модели IFC ─────────── */

export const TakeoffSource = z.enum(['psd', 'ifc', 'manual']);
export const TakeoffRow = z.object({
  id: z.uuid(),
  source: TakeoffSource,
  setId: z.uuid().nullable(),
  setCode: z.string().nullable(),
  sheetId: z.uuid().nullable(),
  sheetNumber: z.string().nullable(),
  ifcGlobalId: z.string().nullable(),
  ifcType: z.string().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  fileName: z.string().nullable(),
  /** Позиций смет, взявших объём из строки */
  itemsCount: z.number().int(),
});
export type TakeoffRow = z.infer<typeof TakeoffRow>;
export const TakeoffList = z.object({ rows: z.array(TakeoffRow), canEdit: z.boolean() });
export type TakeoffList = z.infer<typeof TakeoffList>;

/** Строки из модели IFC (просмотрщик ПСД) или вручную: элемент с тем же GlobalId и единицей обновляется */
export const TakeoffInput = z.object({
  setId: z.uuid().nullable().optional(),
  sheetId: z.uuid().nullable().optional(),
  rows: z
    .array(
      z.object({
        source: z.enum(['ifc', 'manual']),
        ifcGlobalId: z.string().trim().min(1).max(64).nullable().optional(),
        ifcType: z.string().trim().max(80).nullable().optional(),
        code: z.string().trim().max(80).nullable().optional(),
        name: z.string().trim().min(1).max(500),
        unit: z.string().trim().min(1).max(30),
        quantity: z.number().finite().min(0).max(1e9),
      }),
    )
    .min(1)
    .max(5000),
});
export type TakeoffInput = z.infer<typeof TakeoffInput>;

export const TakeoffImportResult = z.object({
  added: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  issues: z.array(z.string()),
});
export type TakeoffImportResult = z.infer<typeof TakeoffImportResult>;

/** Объём позиции из строки ведомости: привязать (или отвязать — null) и, если просят, подставить объём */
export const ItemTakeoffInput = z.object({
  takeoffId: z.uuid().nullable(),
  apply: z.boolean().optional(),
});
export type ItemTakeoffInput = z.infer<typeof ItemTakeoffInput>;

/**
 * Единица без регистра, точек и пробелов: «М3», «м³», «m3» — одна единица. Пояснение нормы после единицы
 * («м3 грунта», «т металлоконструкций») не учитывается — сравнивается первое слово.
 */
const unitBase = (u: string) =>
  (u.trim().toLowerCase().split(/\s+/)[0] ?? '')
    .replace(/³/g, '3')
    .replace(/²/g, '2')
    .replace(/m/g, 'м')
    .replace(/\./g, '')
    .replace(/^шт(ук[аи]?)?$/, 'шт');

/**
 * Во сколько раз объём строки ведомости больше объёма позиции в её единице: «м3» → «100 м3» — 0,01,
 * «1 000 м3 грунта» — 0,001. Разные единицы («шт.» и «м3») — null: объём из такой строки не берётся (СМ-07).
 */
export function takeoffUnitFactor(itemUnit: string, rowUnit: string): number | null {
  const split = (u: string) => {
    const m = /^\s*(\d[\d\s\u00a0]*(?:[.,]\d+)?)\s*(\D.*)$/.exec(u);
    return m
      ? { k: Number(m[1]!.replace(/[\s\u00a0]/g, '').replace(',', '.')), base: unitBase(m[2]!) }
      : { k: 1, base: unitBase(u) };
  };
  const a = split(itemUnit);
  const b = split(rowUnit);
  if (!a.base || a.base !== b.base || !(a.k > 0) || !(b.k > 0)) return null;
  return b.k / a.k;
}
